package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.icons.fa.FaAngleDown
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.core.AppTracking
import info.rezident.app.core.animateValue
import info.rezident.app.data.quiz.RecommendationItem
import info.rezident.app.data.quiz.SpecialtyMetadataRepository
import info.rezident.app.pages.recommendations.ProsConsGenerationError
import info.rezident.app.pages.rotationAnimation
import info.rezident.app.ui.components.animations.RevealEase
import info.rezident.app.ui.components.animations.playOnFirstView
import info.rezident.app.ui.components.animations.rememberMotionRef
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import kotlin.js.json
import kotlin.math.roundToInt
import kotlin.random.Random

// Define a pulse animation for loading skeleton
val skeletonPulseKeyframes = Keyframes {
    0.percent {
        Modifier.opacity(0.3)
    }
    50.percent {
        Modifier.opacity(0.6)
    }
    100.percent {
        Modifier.opacity(0.3)
    }
}

@Composable
private fun RenderAiGenerationFallback(
    aiError: ProsConsGenerationError,
    previewPro: String?,
    previewCon: String?,
    onRetryAi: () -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        AiGenerationErrorBanner(
            errorOccurredAt = aiError.occurredAt,
            errorMessage = aiError.message,
            onRetry = onRetryAi,
            modifier = Modifier.margin(bottom = 16.px)
        )

        ProConsList(
            title = "Puncte Forte",
            recommendations = emptyList(),
            isLocked = true,
            previewText = previewPro,
            isPros = true,
            placeholderCount = 4,
            modifier = Modifier.margin(bottom = 16.px)
        )

        ProConsList(
            title = "Puncte Slabe",
            recommendations = emptyList(),
            isLocked = true,
            previewText = previewCon,
            isPros = false,
            placeholderCount = 4
        )
    }
}

/**
 * Enhanced recommendation card component that matches the MedPath design
 *
 * @param recommendation The recommendation item to display
 * @param isLocked Whether this card should show in locked state
 * @param expandedCardRank The rank of the currently expanded card (for single expansion control)
 * @param onExpandRequest Callback when user requests to expand/collapse this card
 * @param onPaywallClick Callback when compact paywall strip is clicked
 * @param priceText Price text to display in paywall strip
 * @param aiError Optional AI generation error to show inline
 * @param onRetryAi Callback to retry AI generation
 * @param showRanking Whether to show the rank label and the locked match score (ranked variant A)
 * @param modifier Additional modifiers to apply
 */
@Composable
fun EnhancedRecommendationCard(
    recommendation: RecommendationItem,
    isLocked: Boolean = false,
    isQuizUnlocked: Boolean = true,
    expandedCardRank: Int? = null,
    onExpandRequest: ((Int) -> Unit)? = null,
    onUnlockClick: (() -> Unit)? = null,
    onPaywallClick: (() -> Unit)? = null,
    priceText: String? = null,
    aiError: ProsConsGenerationError? = null,
    onRetryAi: (() -> Unit)? = null,
    isGeneratingProsCons: Boolean = false,
    showRanking: Boolean = false,
    modifier: Modifier = Modifier
) {
    if (isLocked) {
        LockedRecommendationCard(
            recommendation = recommendation,
            showRanking = showRanking,
            onClick = onPaywallClick,
            modifier = modifier
        )
    } else {
        RecommendationCardContent(
            recommendation = recommendation,
            isQuizUnlocked = isQuizUnlocked,
            isWorstCard = false,
            showRanking = showRanking,
            expandedCardRank = expandedCardRank,
            onExpandRequest = onExpandRequest,
            onUnlockClick = onUnlockClick,
            onPaywallClick = onPaywallClick,
            priceText = priceText,
            aiError = aiError,
            onRetryAi = onRetryAi,
            isGeneratingProsCons = isGeneratingProsCons,
            modifier = modifier
        )
    }
}

/**
 * Generic list component for pros/cons with customizable styling
 *
 * @param title The title of the list
 * @param recommendations The list items to display
 * @param isGenerating Whether the list is currently being generated
 * @param isLocked Whether to show locked state instead of loading animation
 * @param previewText Optional preview text to show when locked (single item preview)
 * @param isPros Whether this is a pros list (true) or cons list (false)
 * @param aiError Optional AI generation error to show inline
 * @param onRetryAi Callback to retry AI generation
 * @param modifier Additional modifiers to apply
 */
@Composable
internal fun ProConsList(
    title: String,
    recommendations: List<String>,
    isGenerating: Boolean = false,
    isLocked: Boolean = false,
    previewText: String? = null,
    isPros: Boolean = true,
    placeholderCount: Int = 4,
    aiError: ProsConsGenerationError? = null,
    onRetryAi: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val icon = if (isPros) "✓" else "⚠"
    val accentColor = if (isPros) Color("#22c55e") else Color("#f59e0b")
    val titleColor = if (isPros) AppTheme.colors.text else accentColor

    Column(modifier = modifier.fillMaxWidth()) {
        SpanText(
            text = title,
            modifier = AppTheme.typography.body1Medium.toModifier()
                .color(titleColor)
                .margin(bottom = 8.px)
        )

        // Show AI error banner if present
        if (aiError != null && onRetryAi != null) {
            AiGenerationErrorBanner(
                errorOccurredAt = aiError.occurredAt,
                errorMessage = aiError.message,
                onRetry = onRetryAi,
                modifier = Modifier.margin(bottom = 12.px)
            )
        }

        if (isLocked) {
            // Locked state - show preview text if available, otherwise show skeleton
            if (previewText != null) {
                // Show actual preview text
                Row(
                    modifier = Modifier.margin(bottom = 4.px),
                    verticalAlignment = Alignment.Top
                ) {
                    SpanText(
                        text = icon,
                        modifier = Modifier
                            .color(accentColor)
                            .fontSize(16.px)
                            .margin(right = 8.px)
                    )
                    SpanText(
                        text = previewText,
                        modifier = AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.textSecondary)
                            .lineHeight(1.4)
                    )
                }

                // Show placeholder rectangles below the preview text
                repeat(placeholderCount) {
                    Row(
                        modifier = Modifier.margin(bottom = 8.px).fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            text = icon,
                            modifier = Modifier
                                .color(accentColor)
                                .fontSize(16.px)
                                .margin(right = 8.px)
                                .opacity(0.4)
                        )
                        Box(
                            modifier = Modifier
                                .width((60 + Random.nextInt(0, 3) * 20).percent)
                                .height(16.px)
                                .backgroundColor(AppTheme.colors.textTertiary)
                                .borderRadius(4.px)
                                .opacity(0.4)
                        )
                    }
                }
            } else {
                // Fallback: show static skeleton without animation
                repeat(placeholderCount + 1) {
                    Row(
                        modifier = Modifier.margin(bottom = 8.px).fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            text = icon,
                            modifier = Modifier
                                .color(accentColor)
                                .fontSize(16.px)
                                .margin(right = 8.px)
                                .opacity(0.4)
                        )
                        Box(
                            modifier = Modifier
                                .width((60 + Random.nextInt(0, 3) * 20).percent)
                                .height(16.px)
                                .backgroundColor(AppTheme.colors.textTertiary)
                                .borderRadius(4.px)
                                .opacity(0.4)
                        )
                    }
                }
            }
        } else if (isGenerating) {
            // Loading state - show animated skeleton with pulse animation
            SpanText(
                text = "Analiza Detaliată va fi gata în câteva momente",
                modifier = AppTheme.typography.body2.toModifier()
                    .color(AppTheme.colors.textTertiary)
                    .fontStyle(FontStyle.Italic)
                    .margin(bottom = 12.px)
            )

            repeat(5) {
                Row(
                    modifier = Modifier.margin(bottom = 8.px).fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        text = icon,
                        modifier = Modifier
                            .color(accentColor)
                            .fontSize(16.px)
                            .margin(right = 8.px)
                            .animation(
                                skeletonPulseKeyframes.toAnimation(
                                    duration = 1.5.s,
                                    iterationCount = AnimationIterationCount.Infinite
                                )
                            )
                    )
                    Box(
                        modifier = Modifier
                            .width((60 + Random.nextInt(0, 3) * 20).percent)
                            .height(16.px)
                            .backgroundColor(AppTheme.colors.textTertiary)
                            .borderRadius(4.px)
                            .animation(
                                skeletonPulseKeyframes.toAnimation(
                                    duration = 1.5.s,
                                    iterationCount = AnimationIterationCount.Infinite
                                )
                            )
                    )
                }
            }
        } else {
            // Loaded state - show actual content
            recommendations.forEach { recommendation ->
                Row(
                    modifier = Modifier.margin(bottom = 4.px),
                    verticalAlignment = Alignment.Top
                ) {
                    SpanText(
                        text = icon,
                        modifier = Modifier
                            .color(accentColor)
                            .fontSize(16.px)
                            .margin(right = 8.px)
                    )
                    SpanText(
                        text = recommendation,
                        modifier = AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.textSecondary)
                            .lineHeight(1.4)
                    )
                }
            }
        }
    }
}

/**
 * Generic card showing recommendation details with conditional styling
 *
 * @param recommendation The recommendation item to display
 * @param isQuizUnlocked Whether the quiz is unlocked (for pros/cons display)
 * @param isWorstCard Whether this is a worst/incompatible recommendation card
 * @param expandedCardRank The rank of the currently expanded card (for single expansion control)
 * @param onExpandRequest Callback when user requests to expand/collapse this card
 * @param onUnlockClick Callback when unlock button is clicked (for locked state)
 * @param onPaywallClick Callback when compact paywall strip is clicked
 * @param priceText Price text to display in paywall strip
 * @param aiError Optional AI generation error to show inline
 * @param onRetryAi Callback to retry AI generation
 * @param showRanking Whether to show the rank label and the locked match score (ranked variant A)
 * @param modifier Additional modifiers to apply
 */
@Composable
fun RecommendationCardContent(
    recommendation: RecommendationItem,
    isQuizUnlocked: Boolean = true,
    isWorstCard: Boolean = false,
    isLocked: Boolean = false,
    showRanking: Boolean = false,
    expandedCardRank: Int? = null,
    onExpandRequest: ((Int) -> Unit)? = null,
    onUnlockClick: (() -> Unit)? = null,
    onPaywallClick: (() -> Unit)? = null,
    priceText: String? = null,
    aiError: ProsConsGenerationError? = null,
    onRetryAi: (() -> Unit)? = null,
    isGeneratingProsCons: Boolean = false,
    scoreAnimationDelaySeconds: Double? = null,
    modifier: Modifier = Modifier
) {
    // The score shown by the number. Starts at the final score, so it's right in the pre-rendered page and whenever
    // it isn't animated. If [scoreAnimationDelaySeconds] is set, the bar fills up and the number counts up from 0
    // once the card scrolls into view, that many seconds after
    var displayedScore by remember(recommendation.matchScore) { mutableStateOf(recommendation.matchScore) }
    val scoreAnimationRef = rememberMotionRef<HTMLElement> { row ->
        val bar = row.querySelector("[$SCORE_BAR_ATTRIBUTE]") as? HTMLElement
        if (scoreAnimationDelaySeconds == null || bar == null) {
            {}
        } else {
            animateScoreOnScroll(row, bar, recommendation.matchScore, scoreAnimationDelaySeconds) { displayedScore = it }
        }
    }

    // Use external expansion state if provided, otherwise use internal state
    var internalExpanded by remember(recommendation.rank) { mutableStateOf(false) }
    val isExpanded = if (onExpandRequest != null) {
        expandedCardRank == recommendation.rank
    } else {
        internalExpanded
    }

    // Remember the randomly selected preview texts so they don't change on re-expansion
    // Must be outside the isExpanded conditional to persist across collapse/expand cycles
    val rememberedPreviewPro = remember(recommendation.rank) { recommendation.getFirstPreviewPro() }
    val rememberedPreviewCon = remember(recommendation.rank) { recommendation.getFirstPreviewCon() }

    // All cards now use the same border style: 1px dark border, no shadow
    val borderModifier = Modifier.border(1.px, LineStyle.Solid, AppTheme.colors.textTertiary)
    val pointerModifier = if (isWorstCard) {
        Modifier  // Worst cards are not clickable
    } else {
        Modifier.cursor(Cursor.Pointer)
    }
    val shouldShowLocked = if (isWorstCard) isLocked else !isQuizUnlocked
    val hasGeneratedContent = recommendation.pros.isNotEmpty() || recommendation.cons.isNotEmpty()
    val hasAiError = aiError != null && onRetryAi != null
    val hasAnswerCategories = recommendation.perfectAnswersHigh.isNotEmpty() ||
            recommendation.perfectAnswersLow.isNotEmpty() ||
            recommendation.differentAnswersHigh.isNotEmpty() ||
            recommendation.differentAnswersLow.isNotEmpty()
    val isGeneratingForCard = isGeneratingProsCons && !hasGeneratedContent && hasAnswerCategories

    Box(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.surface)
            .borderRadius(AppTheme.shapes.mediumRound)
            .padding(20.px)
            .then(pointerModifier)
            .then(borderModifier)
            .id("recommendation-card-${recommendation.rank}")
            .onClick {
                // Worst cards are not expandable
                if (!isWorstCard) {
                    val next = !isExpanded

                    // Use external callback if provided, otherwise update internal state
                    if (onExpandRequest != null) {
                        onExpandRequest(recommendation.rank)
                    } else {
                        // Ignore unused warning
                        internalExpanded = next
                    }

                    // Log expand / collapse details click (only for unlocked cards)
                    AppTracking.logEvent(
                        AppTracking.EVENT_RECOMMENDATIONS_DETAILS_CLICK,
                        mapOf(
                            "rank" to recommendation.rank,
                            "name" to recommendation.name,
                            "action" to if (next) "expand" else "collapse"
                        )
                    )
                }
            }
    ) {
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.TopCenter
        ) {
            // Left side: Content
            Column(Modifier.fillMaxWidth()) {
                // Rank badge (ranked variant only, not for worst cards)
                if (showRanking && !isWorstCard) {
                    SpanText(
                        text = "Recomandarea #${recommendation.rank}",
                        modifier = AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.textTertiary)
                            .margin(bottom = 4.px)
                    )
                }

                // Specialty name
                SpanText(
                    text = recommendation.name,
                    modifier = AppTheme.typography.h2.toModifier()
                        .color(AppTheme.colors.text)
                        .fontWeight(FontWeight.SemiBold)
                        .margin(bottom = 8.px)
                )

                // Specialty description
                val description = SpecialtyMetadataRepository.getDescription(recommendation.name)
                if (description != null) {
                    SpanText(
                        text = description,
                        modifier = AppTheme.typography.body1.toModifier()
                            .color(AppTheme.colors.textSecondary)
                            .lineHeight(1.5)
                            .margin(bottom = 16.px)
                    )
                }

                // Match score section
                Column(modifier = Modifier.fillMaxWidth()) {
                    Row(
                        modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
                        horizontalArrangement = Arrangement.spacedBy(20.px),
                        verticalAlignment = Alignment.CenterVertically,
                        ref = scoreAnimationRef
                    ) {
                        SpanText(
                            text = "Scor Potrivire",
                            modifier = AppTheme.typography.body2.toModifier()
                                .color(AppTheme.colors.textTertiary)
                        )

                        // Progress bar
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(10.px)
                                .backgroundColor(Color.lightgray)
                                .borderRadius(6.px)
                        ) {
                            Box(
                                modifier = Modifier
                                    .width((recommendation.matchScore).percent)
                                    .height(10.px)
                                    .backgroundColor(getScoreColor(recommendation.matchScore))
                                    .borderRadius(6.px)
                                    .attr(SCORE_BAR_ATTRIBUTE, "")
                            )
                        }

                        SpanText(
                            text = "$displayedScore%",
                            modifier = AppTheme.typography.body2.toModifier()
                                .color(AppTheme.colors.text)
                                .fontWeight(FontWeight.Medium)
                                // Equal-width digits, so the bar doesn't wobble while the number counts up
                                .styleModifier { property("font-variant-numeric", "tabular-nums") }
                        )
                    }

                    // Characteristic tags (only for unlocked cards)
                    if (!isWorstCard) {
                        CharacteristicTags(
                            characteristics = recommendation.characteristics,
                            maxVisibleTags = 4
                        )
                    }

                    // Expandable details section (shown above the bottom controls)
                    // Only show if there are pros or cons (or if they're being generated) or if there are answer category IDs available
                    val hasPros = recommendation.pros.isNotEmpty() || isGeneratingForCard
                    val hasCons = recommendation.cons.isNotEmpty() || isGeneratingForCard
                    val shouldShowAiFallback = hasAiError && !shouldShowLocked && !hasGeneratedContent
                    val hasDetails = hasPros || hasCons || hasAnswerCategories || shouldShowAiFallback

                    if (isExpanded && hasDetails) {
                        Column(modifier = Modifier.margin(top = 16.px).fillMaxWidth()) {
                            // Show locked state with unlock CTA:
                            // - For regular cards: when quiz is not unlocked
                            // - For worst cards: when specifically marked as locked
                            if (shouldShowLocked) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(top = 14.px)
                                        .borderRadius(8.px),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    SpanText(
                                        text = "Află de ce ți se potrivește această specialitate în Analiza Detaliată",
                                        modifier = AppTheme.typography.body1Medium.toModifier()
                                            .color(AppTheme.colors.text)
                                            .textAlign(TextAlign.Center)
                                            .margin(bottom = 20.px)
                                    )

                                    // Show locked pros/cons lists with preview texts from answer categories
                                    ProConsList(
                                        title = "Puncte Forte",
                                        recommendations = emptyList(),
                                        isGenerating = true,
                                        isLocked = true,
                                        previewText = rememberedPreviewPro,
                                        isPros = true,
                                        placeholderCount = if (onPaywallClick != null) 2 else 4
                                    )

                                    // Informational text about locked content
                                    SpanText(
                                        text = "Mai multe Puncte Forte și Slabe sunt disponibile în Analiza Detaliată",
                                        modifier = AppTheme.typography.body2.toModifier()
                                            .color(AppTheme.colors.textTertiary)
                                            .fillMaxWidth()
                                            .textAlign(TextAlign.Start)
                                            .fontStyle(FontStyle.Italic)
                                            .margin(top = 2.px, bottom = 20.px)
                                    )

                                    // Compact paywall strip (mobile only)
                                    if (onPaywallClick != null) {
                                        CompactPaywallStrip(
                                            priceText = priceText,
                                            onClick = onPaywallClick,
                                            id = "compact-paywall-strip"
                                        )
                                    }

                                    Spacer(Modifier.height(20.px))

                                    ProConsList(
                                        title = "Puncte Slabe",
                                        recommendations = emptyList(),
                                        isGenerating = true,
                                        isLocked = true,
                                        previewText = rememberedPreviewCon,
                                        isPros = false,
                                        placeholderCount = if (onPaywallClick != null) 2 else 4
                                    )
                                }
                            } else {
                                // Quiz is unlocked - show actual content or loading state
                                // Pros section - only show if there are pros or generating
                                if (shouldShowAiFallback) {
                                    RenderAiGenerationFallback(
                                        aiError = aiError,
                                        previewPro = rememberedPreviewPro,
                                        previewCon = rememberedPreviewCon,
                                        onRetryAi = onRetryAi
                                    )
                                } else {
                                    if (hasPros) {
                                        ProConsList(
                                            title = "Puncte Forte",
                                            recommendations = recommendation.pros,
                                            isGenerating = isGeneratingForCard,
                                            isLocked = false,
                                            isPros = true,
                                            aiError = aiError,
                                            onRetryAi = onRetryAi
                                        )
                                    }

                                    // Cons section - only show if there are cons or generating
                                    if (hasCons) {
                                        ProConsList(
                                            title = "Puncte Slabe",
                                            recommendations = recommendation.cons,
                                            isGenerating = isGeneratingForCard,
                                            isLocked = false,
                                            isPros = false,
                                            aiError = aiError,
                                            onRetryAi = onRetryAi
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // Bottom divider and toggle control area (always stays at the bottom of the card)
                    // Don't show expand button for worst cards
                    if (hasDetails && !isWorstCard) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(2.px)
                                .backgroundColor(AppTheme.colors.text)
                                .opacity(0.5)
                                .margin(top = 20.px)
                                .borderRadius(AppTheme.shapes.bigRound)
                        )

                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 18.px)
                                .justifyContent(JustifyContent.Center),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                text = if (isExpanded) "Ascunde Analiza Detaliată" else "De ce mi se potrivește?",
                                modifier = AppTheme.typography.body2.toModifier()
                                    .color(AppTheme.colors.text)
                                    .then(if (!isExpanded) Modifier.fontWeight(FontWeight.Bold) else Modifier)
                            )
                            key(isExpanded) {
                                FaAngleDown(
                                    AppTheme.typography.body2.toModifier()
                                        .color(AppTheme.colors.icon)
                                        .margin(left = 8.px)
                                        .run {
                                            if (isExpanded) {
                                                animation(
                                                    rotationAnimation.toAnimation(
                                                        duration = 50.ms,
                                                        direction = if (isExpanded) AnimationDirection.Normal else AnimationDirection.Reverse,
                                                        fillMode = AnimationFillMode.Forwards
                                                    )
                                                )
                                            } else this
                                        }
                                )
                            }
                        }
                    }
                }
            }

            // Right side: Icon
            Box(
                modifier = Modifier
                    .size(36.px)
                    .backgroundColor(AppTheme.colors.section)
                    .borderRadius(6.px)
                    .margin(left = 16.px)
                    .align(Alignment.TopEnd)
                    .flexShrink(0),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    text = if (isWorstCard) "⚠️" else getSpecialtyIcon(recommendation.name),
                    modifier = Modifier.fontSize(24.px)
                )
            }
        }
    }
}

/**
 * Card showing locked/blurred content
 *
 * @param showRanking Whether to show the rank label and the locked match score (ranked variant A)
 */
@Composable
fun LockedRecommendationCard(
    recommendation: RecommendationItem,
    isWorstCard: Boolean = false,
    showRanking: Boolean = false,
    onClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.textTertiary)
            .borderRadius(AppTheme.shapes.mediumRound)
            .padding(20.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { _ ->
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_LOCKED_CARD_CLICK,
                    mapOf(
                        "rank" to recommendation.rank,
                        "name" to recommendation.name,
                        "is_worst_card" to isWorstCard
                    )
                )
                if (onClick != null) {
                    onClick()
                } else {
                    window.location.assign("#deblocheaza")
                }
            }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top
        ) {
            // Left side: Content
            Column(
                modifier = Modifier.weight(1f)
            ) {
                // Rank badge (ranked variant only, not for worst cards)
                if (showRanking && !isWorstCard) {
                    SpanText(
                        text = "Recomandarea #${recommendation.rank}",
                        modifier = AppTheme.typography.body1.toModifier()
                            .color(AppTheme.colors.surface)
                            .margin(bottom = 4.px)
                            .zIndex(3)
                    )
                }

                // Specialty name
                SpanText(
                    text = "Specialitate",
                    modifier = AppTheme.typography.h2.toModifier()
                        .color(AppTheme.colors.surface)
                        .fontWeight(FontWeight.SemiBold)
                        .margin(bottom = 16.px)
                )

                // Placeholder content for other locked cards
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(20.px)
                        .backgroundColor(AppTheme.colors.header)
                        .borderRadius(4.px)
                        .margin(bottom = 8.px)
                )
                Box(
                    modifier = Modifier
                        .width(80.percent)
                        .height(20.px)
                        .backgroundColor(AppTheme.colors.header)
                        .borderRadius(4.px)
                        .margin(bottom = 16.px)
                )

                // Progress bar section (matching unlocked cards)
                Column(modifier = Modifier.fillMaxWidth()) {
                    Row(
                        modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
                        horizontalArrangement = Arrangement.spacedBy(20.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            text = "Scor Potrivire",
                            modifier = AppTheme.typography.body2.toModifier()
                                .color(AppTheme.colors.surface)
                        )

                        // Progress bar - the real score is only previewed in the ranked variant
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(10.px)
                                .backgroundColor(AppTheme.colors.header)
                                .borderRadius(6.px)
                        ) {
                            if (showRanking) {
                                Box(
                                    modifier = Modifier
                                        .width((recommendation.matchScore).percent)
                                        .height(10.px)
                                        .backgroundColor(getScoreColor(recommendation.matchScore))
                                        .borderRadius(6.px)
                                )
                            }
                        }

                        if (showRanking) {
                            SpanText(
                                text = "${recommendation.matchScore}%",
                                modifier = AppTheme.typography.body2.toModifier()
                                    .color(AppTheme.colors.surface)
                                    .fontWeight(FontWeight.Medium)
                            )
                        }
                    }
                }

                // Placeholder tags
                Row(
                    horizontalArrangement = Arrangement.spacedBy(6.px),
                    modifier = Modifier.margin(bottom = 16.px)
                ) {
                    repeat(3) { index ->
                        Box(
                            modifier = Modifier
                                .backgroundColor(AppTheme.colors.header)
                                .borderRadius(16.px)
                                .width((50 + index * 15).px)
                                .height(24.px)
                        )
                    }
                }
            }
        }

        // Lock icon
        Box(
            modifier = Modifier
                .align(Alignment.Center)
                .backgroundColor(AppTheme.colors.surface)
                .size(48.px)
                .padding(24.px)
                .borderRadius(50.percent)
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .justifyContent(JustifyContent.Center)
                .boxShadow(0.px, 2.px, 8.px, 0.px, AppTheme.colors.textTertiary)
                .zIndex(2),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                text = "🔒",
                modifier = Modifier.fontSize(20.px)
            )
        }
    }
}

private const val SCORE_BAR_ATTRIBUTE = "data-score-bar"

/**
 * Fills the score [bar] from 0 while the number next to it counts up to [score], the first time [row] scrolls into
 * view, in the same way as the quiz progress bar on the landing page.
 *
 * The bar follows the exact animated value rather than the rounded number shown next to it, so it fills smoothly
 * instead of in 1% steps.
 */
private fun animateScoreOnScroll(
    row: HTMLElement,
    bar: HTMLElement,
    score: Int,
    delay: Double,
    onNumberUpdate: (Int) -> Unit
): () -> Unit = playOnFirstView(
    row,
    prepare = {
        bar.style.width = "0%"
        onNumberUpdate(0)
    },
    play = {
        var shownNumber = 0
        animateValue(
            0.0,
            score.toDouble(),
            json(
                "duration" to 0.9,
                "delay" to delay,
                "ease" to RevealEase,
                "onUpdate" to { value: Double ->
                    bar.style.width = "$value%"
                    // Only re-render the number when it actually changes
                    val number = value.roundToInt()
                    if (number != shownNumber) {
                        shownNumber = number
                        onNumberUpdate(number)
                    }
                }
            )
        )
    }
)

/**
 * Returns a color for the progress bar based on match score
 */
@Composable
private fun getScoreColor(score: Int): CSSColorValue {
    return when {
        score >= 80 -> Color("#22c55e") // Green
        score >= 70 -> Color("#F3E100") // Yellow
        score >= 60 -> Color("#f59e0b") // Orange
        else -> Color("#ef4444") // Red
    }
}

/**
 * Returns an appropriate icon for different medical specialties
 */
private fun getSpecialtyIcon(specialtyName: String): String {
    val name: String = specialtyName.lowercase()
    return when {
        // Obstetrics & Gynecology / Neonatology
        name.contains("neonat") -> "🍼"
        name.contains("obstet") || name.contains("obstetr") || name.contains("gineco") || name.contains("ginecol") || name.contains(
            "gyneco"
        ) -> "🤰"

        // Pediatrics
        name.contains("pediatri") -> "👶"

        // Cardiology / Vascular
        name.contains("cardio") -> "🫀"
        name.contains("vascular") || name.contains("vascul") -> "🩸"

        // Pulmonology / Thoracic
        name.contains("pneumo") || name.contains("pulmon") || name.contains("toracic") -> "🫁"

        // Neurology / Neurosurgery / Psychiatry
        name.contains("neurochir") -> "🧠"
        name.contains("neuro") -> "🧠"
        name.contains("psihia") || name.contains("psihiat") || name.contains("psychiat") -> "🧠"

        // Dermatology
        name.contains("dermat") -> "🧴"

        // Ophthalmology
        name.contains("oftalm") || name.contains("ophthalm") -> "👁️"

        // ENT (ORL / Otolaryngology)
        name.contains("orl") || name.contains("otolaring") || name.contains("oto-rino") || name.contains("otorino") || name.contains(
            "otolaryng"
        ) -> "👂"

        // Orthopedics / Rheumatology
        name.contains("ortoped") || name.contains("orthop") -> "🦴"
        name.contains("reumat") || name.contains("rheumat") -> "🦵"

        // Urology / Nephrology
        name.contains("urolog") -> "💧"
        name.contains("nefrolog") || name.contains("renal") || name.contains("rinich") -> "🚰"

        // Gastroenterology / Hepatology / Digestive
        name.contains("gastro") || name.contains("hepat") || name.contains("digest") -> "🍽️"

        // Endocrinology / Diabetes / Nutrition
        name.contains("endocr") -> "🧬"
        name.contains("diabet") -> "🍬"
        name.contains("nutri") || name.contains("diet") -> "🥗"

        // Allergology / Immunology
        name.contains("alerg") || name.contains("allerg") || name.contains("imun") || name.contains("immun") -> "🤧"

        // Infectious Diseases
        name.contains("infec") || name.contains("infect") -> "🦠"

        // Oncology / Hematology
        name.contains("onco") -> "🎗️"
        name.contains("hemat") || name.contains("hema") -> "🩸"

        // Radiology / Imaging / Nuclear / Radiotherapy
        name.contains("radioter") -> "☢️"
        name.contains("radiolog") || name.contains("imagist") || name.contains("imag") || name.contains("x-ray") -> "🩻"
        name.contains("nuclear") || name.contains("nucle") -> "☢️"

        // Pathology / Anatomy
        name.contains("patolog") || name.contains("anatom") -> "🔬"

        // Anesthesiology
        name.contains("anestez") || name.contains("anesth") -> "💉"

        // Dentistry / Maxillofacial
        name.contains("stomatolog") || name.contains("dent") || name.contains("maxilo") -> "🦷"

        // Family / Internal / General Medicine
        name.contains("familie") || name.contains("family") -> "👥"
        name.contains("medicină intern") || name.contains("medicina intern") || name.contains("internal") -> "🩺"

        // Emergency Medicine
        name.contains("urgenț") || name.contains("urgent") || name.contains("urgen") -> "🚑"

        // Geriatrics
        name.contains("geriatr") -> "👵"

        // Rehabilitation / Physiotherapy
        name.contains("recuper") || name.contains("reabilit") || name.contains("fizioter") || name.contains("kineto") || name.contains(
            "physio"
        ) -> "🧘"

        // Occupational Medicine
        name.contains("muncii") || name.contains("ocupa") || name.contains("occupational") -> "🧑‍🏭"

        // Sports Medicine
        name.contains("sport") -> "🏃"

        // Laboratory
        name.contains("laborator") || name.contains("lab.") || name.contains("laboratory") -> "🧪"

        // Pharmacy
        name.contains("farmac") || name.contains("pharma") -> "💊"

        // Public Health / Epidemiology
        name.contains("epidemiolog") || name.contains("epidemio") || name.contains("sănătate public") || name.contains(
            "sanatate public"
        ) || name.contains(
            "public health"
        ) -> "📊"

        // General Surgery (fallback for surgical terms)
        name.contains("chirurg") || name.contains("surgery") -> "🏥"

        else -> "🩺" // Generic medical icon
    }
}

/**
 * AI Generation Error Banner with 1-minute cooldown retry button
 */
@Composable
fun AiGenerationErrorBanner(
    errorOccurredAt: Long,
    errorMessage: String,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier
) {
    var canRetry by remember { mutableStateOf(false) }
    var remainingSeconds by remember { mutableStateOf(60) }

    // Calculate if retry is available (1 minute = 60000ms)
    LaunchedEffect(errorOccurredAt) {
        while (true) {
            val currentTime = js("Date.now()") as Double
            val elapsedMs = currentTime - errorOccurredAt
            val elapsedSeconds = (elapsedMs / 1000).toInt()

            if (elapsedSeconds >= 60) {
                canRetry = true
                remainingSeconds = 0
                break
            } else {
                canRetry = false
                remainingSeconds = 60 - elapsedSeconds
            }

            delay(1000) // Update every second
        }
    }

    Column(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(Color("#fef3c7"))
            .border(1.px, LineStyle.Solid, Color("#f59e0b"))
            .borderRadius(8.px)
            .padding(16.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            text = errorMessage,
            modifier = AppTheme.typography.body2.toModifier()
                .color(Color("#92400e"))
                .textAlign(TextAlign.Center)
                .margin(bottom = 12.px)
        )

        com.varabyte.kobweb.silk.components.forms.Button(
            onClick = { onRetry() },
            modifier = Modifier
                .backgroundColor(if (canRetry) AppTheme.colors.accent else AppTheme.colors.textTertiary)
                .borderRadius(6.px)
                .padding(leftRight = 16.px, topBottom = 8.px),
            enabled = canRetry
        ) {
            SpanText(
                text = if (canRetry) "Reîncearcă" else "Reîncearcă în ${remainingSeconds}s",
                modifier = AppTheme.typography.body2.toModifier()
                    .color(if (canRetry) AppTheme.colors.onAccent else AppTheme.colors.surface)
            )
        }
    }
}
